import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Minus, Plus, RotateCcw, ShoppingBag, Trash2 } from "lucide-react";
import { retailerApi, formatApiError } from "../../lib/api";
import { LOGO_PINK } from "../../lib/data";

const inputCls = "w-full rounded-xl border border-zinc-300 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-pink";
const labelCls = "block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1";

const StatusPill = ({ value }) => (
    <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase ${
        ["PAID", "FULFILLED"].includes(value) ? "bg-green-100 text-green-700"
        : value === "PENDING PAYMENT" ? "bg-amber-100 text-amber-700"
        : value === "CANCELLED" ? "bg-zinc-200 text-zinc-500"
        : "bg-pink-soft text-pink-dark"
    }`}>{value}</span>
);

const CatalogCard = ({ product, qty, onQty, onAdd }) => {
    const caseQty = product.case_qty > 1 ? product.case_qty : 1;
    const moq = product.moq > 1 ? product.moq : caseQty;
    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 flex flex-col gap-3" data-testid={`catalog-card-${product.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="font-oswald uppercase text-lg leading-tight">{product.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">{product.category}{product.wholesale_sku ? ` · ${product.wholesale_sku}` : ""}</p>
                </div>
                <p className="font-oswald text-xl text-pink-dark whitespace-nowrap">${Number(product.wholesale_price).toFixed(2)}<span className="text-xs text-zinc-400">/unit</span></p>
            </div>
            <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <span className="rounded-full bg-zinc-100 px-2.5 py-1">Case of {caseQty}</span>
                <span className="rounded-full bg-zinc-100 px-2.5 py-1">MOQ {moq}</span>
            </div>
            <div className="flex items-center gap-2 mt-auto">
                <button onClick={() => onQty(product, Math.max(0, qty - caseQty))} data-testid={`catalog-minus-${product.id}`} className="rounded-full border border-zinc-300 p-2 hover:border-pink"><Minus size={14} /></button>
                <span className="w-12 text-center font-oswald text-lg" data-testid={`catalog-qty-${product.id}`}>{qty}</span>
                <button onClick={() => onQty(product, (qty || 0) + caseQty)} data-testid={`catalog-plus-${product.id}`} className="rounded-full border border-zinc-300 p-2 hover:border-pink"><Plus size={14} /></button>
                <button
                    onClick={() => onAdd(product)}
                    disabled={!qty || qty < moq}
                    data-testid={`catalog-add-${product.id}`}
                    className="ml-auto rounded-full bg-ink text-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-pink hover:text-ink transition-colors disabled:opacity-40"
                >
                    Add
                </button>
            </div>
        </div>
    );
};

export const RetailerPortal = () => {
    const [user, setUser] = useState(null);
    const [tab, setTab] = useState("catalog");
    const [catalog, setCatalog] = useState([]);
    const [orders, setOrders] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [cart, setCart] = useState([]);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Retailer Portal — Showtime Carnival Co.";
        retailerApi.get("/retailer/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate("/retailer/login"));
    }, [navigate]);

    const loadCatalog = () => retailerApi.get("/retailer/catalog").then((r) => setCatalog(r.data || [])).catch(() => {});
    const loadOrders = () => retailerApi.get("/retailer/orders").then((r) => setOrders(r.data || [])).catch(() => {});
    useEffect(() => { if (user) { loadCatalog(); loadOrders(); } }, [user]);

    const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.wholesale_price * i.qty, 0), [cart]);

    const setQty = (product, qty) => setQuantities((q) => ({ ...q, [product.id]: qty }));

    const addToCart = (product) => {
        const qty = quantities[product.id] || 0;
        if (!qty) return;
        setCart((c) => {
            const existing = c.find((i) => i.id === product.id);
            if (existing) return c.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
            return [...c, { id: product.id, name: product.name, wholesale_price: product.wholesale_price, qty }];
        });
        setQty(product, 0);
        toast.success(`${product.name} × ${qty} added to order.`);
    };

    const reorder = (order) => {
        const available = new Set(catalog.map((p) => p.id));
        const items = order.items.filter((i) => available.has(i.product_id));
        if (!items.length) {
            toast.error("None of those items are in the current wholesale catalog.");
            return;
        }
        setCart(items.map((i) => ({ id: i.product_id, name: i.name, wholesale_price: i.price, qty: i.qty })));
        setTab("catalog");
        window.scrollTo(0, 0);
        toast.success(`Cart loaded from ${order.order_number}. Review and submit.`);
    };

    const submitOrder = async () => {
        if (!cart.length) return;
        setSubmitting(true);
        try {
            const res = await retailerApi.post("/retailer/orders", {
                items: cart.map((i) => ({ id: i.id, qty: i.qty })),
                notes: notes || undefined,
            });
            toast.success(`Order request ${res.data.order_number} submitted — pending payment.`);
            setCart([]);
            setNotes("");
            loadOrders();
            setTab("orders");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        try {
            await retailerApi.post("/retailer/change-password", pwForm);
            toast.success("Password updated.");
            setPwForm({ current_password: "", new_password: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const logout = () => {
        localStorage.removeItem("showtime_retailer_token");
        navigate("/retailer/login");
    };

    if (!user) return <div className="min-h-[100svh] bg-ink" />;

    const TABS = [["catalog", "Shop Wholesale"], ["orders", `Orders${orders.length ? ` (${orders.length})` : ""}`], ["account", "Account"]];

    return (
        <section className="min-h-[100svh] bg-paper pt-24 md:pt-28 pb-20" data-testid="retailer-portal">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_PINK} alt="Showtime" className="h-12 w-auto" />
                        <div>
                            <h1 className="font-oswald uppercase text-2xl">{user.business_name || "Retailer Portal"}</h1>
                            <p className="text-xs text-zinc-500">
                                {user.email}
                                {(user.partner || user.territory || user.cohort) && (
                                    <span> · {[user.partner, user.territory, user.cohort].filter(Boolean).join(" · ")}</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <button onClick={logout} data-testid="retailer-logout-button" className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-600 hover:border-pink">
                        <LogOut size={14} /> Logout
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-8">
                    {TABS.map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            data-testid={`retailer-tab-${key}`}
                            className={`rounded-full px-5 py-2.5 font-oswald uppercase tracking-widest text-xs transition-colors ${
                                tab === key ? "bg-ink text-white" : "bg-white border border-zinc-300 text-zinc-600 hover:border-pink"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                    {orders.length > 0 && tab === "catalog" && (
                        <button
                            onClick={() => reorder(orders[0])}
                            data-testid="reorder-last-order-button"
                            className="ml-auto inline-flex items-center gap-2 rounded-full bg-pink px-5 py-2.5 font-oswald uppercase tracking-widest text-xs text-ink hover:bg-ink hover:text-white transition-colors"
                        >
                            <RotateCcw size={13} /> Reorder Last Order ({orders[0].order_number})
                        </button>
                    )}
                </div>

                {tab === "catalog" && (
                    <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr] items-start">
                        <div className="grid gap-4 sm:grid-cols-2" data-testid="retailer-catalog">
                            {catalog.length === 0 && (
                                <p className="text-zinc-400 text-sm sm:col-span-2">The wholesale catalog is being stocked. Check back soon.</p>
                            )}
                            {catalog.map((p) => (
                                <CatalogCard key={p.id} product={p} qty={quantities[p.id] || 0} onQty={setQty} onAdd={addToCart} />
                            ))}
                        </div>
                        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:sticky lg:top-28" data-testid="retailer-cart">
                            <p className="font-oswald uppercase text-lg mb-4 flex items-center gap-2"><ShoppingBag size={18} /> Order Request</p>
                            {cart.length === 0 && <p className="text-zinc-400 text-sm">Your order request is empty. Add cases from the catalog.</p>}
                            <div className="space-y-3">
                                {cart.map((i) => (
                                    <div key={i.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`cart-line-${i.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-semibold truncate">{i.name}</p>
                                            <p className="text-xs text-zinc-500">{i.qty} units × ${Number(i.wholesale_price).toFixed(2)}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="font-oswald">${(i.wholesale_price * i.qty).toFixed(2)}</span>
                                            <button onClick={() => setCart((c) => c.filter((x) => x.id !== i.id))} data-testid={`cart-remove-${i.id}`} className="text-zinc-400 hover:text-red-500"><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {cart.length > 0 && (
                                <>
                                    <div className="border-t border-zinc-200 mt-4 pt-4 flex items-center justify-between">
                                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Estimated Total</span>
                                        <span className="font-oswald text-xl" data-testid="cart-total">${cartTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor="rt-notes" className={labelCls}>Delivery notes (optional)</label>
                                        <textarea id="rt-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="order-notes-input" placeholder="Delivery window, loading dock, etc." />
                                    </div>
                                    <button
                                        onClick={submitOrder}
                                        disabled={submitting}
                                        data-testid="submit-order-request-button"
                                        className="mt-4 w-full rounded-full bg-pink py-3.5 font-oswald uppercase tracking-widest text-sm text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
                                    >
                                        {submitting ? "Submitting..." : "Submit Order Request"}
                                    </button>
                                    <p className="text-[11px] text-zinc-400 mt-3">Submitted as PENDING PAYMENT — we'll confirm availability and email your invoice.</p>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {tab === "orders" && (
                    <div className="space-y-4" data-testid="retailer-orders">
                        {orders.length === 0 && <p className="text-zinc-400 text-sm">No orders yet. Build your first order request in the catalog.</p>}
                        {orders.map((o) => (
                            <div key={o.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`retailer-order-${o.order_number}`}>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="font-oswald uppercase text-lg">{o.order_number} <StatusPill value={o.status} /></p>
                                        <p className="text-xs text-zinc-500 mt-1">{o.created_at ? new Date(o.created_at).toLocaleString() : ""} · {o.items?.length || 0} item(s) · ${Number(o.total || 0).toFixed(2)}</p>
                                        <p className="text-xs text-zinc-500 mt-1">{o.items?.map((i) => `${i.name} × ${i.qty}`).join(" · ")}</p>
                                        {o.notes && <p className="text-xs text-zinc-400 mt-1">"{o.notes}"</p>}
                                    </div>
                                    <button onClick={() => reorder(o)} data-testid={`reorder-${o.order_number}`} className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:border-pink">
                                        <RotateCcw size={12} /> Reorder
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "account" && (
                    <div className="grid gap-8 lg:grid-cols-2 items-start" data-testid="retailer-account">
                        <div className="rounded-2xl border border-zinc-200 bg-white p-6">
                            <p className="font-oswald uppercase text-lg mb-4">Business Profile</p>
                            <dl className="space-y-3 text-sm">
                                {[["Business", user.business_name], ["Contact", user.name], ["Email", user.email],
                                  ["Partner", user.partner], ["Territory", user.territory], ["Pilot Cohort", user.cohort]].map(([label, value]) => (
                                    <div key={label} className="flex justify-between gap-4 border-b border-zinc-100 pb-2">
                                        <dt className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 pt-0.5">{label}</dt>
                                        <dd className="font-medium text-right">{value || "—"}</dd>
                                    </div>
                                ))}
                            </dl>
                            <p className="text-[11px] text-zinc-400 mt-4">Need to change your business details? Contact your Showtime rep.</p>
                        </div>
                        <form onSubmit={changePassword} className="rounded-2xl border border-zinc-200 bg-white p-6" data-testid="retailer-password-form">
                            <p className="font-oswald uppercase text-lg mb-4">Change Password</p>
                            <label htmlFor="rt-cur-pw" className={labelCls}>Current password</label>
                            <input id="rt-cur-pw" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))} className={inputCls + " mb-3"} data-testid="current-password-input" />
                            <label htmlFor="rt-new-pw" className={labelCls}>New password (min 8 characters)</label>
                            <input id="rt-new-pw" type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} className={inputCls + " mb-4"} data-testid="new-password-input" />
                            <button type="submit" data-testid="change-password-button" className="w-full rounded-full bg-ink py-3 font-oswald uppercase tracking-widest text-sm text-white hover:bg-pink hover:text-ink transition-colors">
                                Update Password
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </section>
    );
};
